import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Search, Loader, UserCog, Pencil, KeyRound,
  Trash2, UserX, UserCheck, ShieldAlert, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { users as usersApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogHeader, DialogBody, DialogFooter, Field, SwitchRow } from '../components/Dialog';

const PAGE_SIZE = 25;

const ROLE_STYLES = {
  admin: { bg: 'var(--badge-danger-bg)', color: 'var(--accent-danger)', label: 'Admin' },
  faculty: { bg: 'var(--badge-info-bg)', color: 'var(--accent-primary)', label: 'Faculty' },
  student: { bg: 'var(--bg-tertiary)', color: 'var(--text-muted)', label: 'Student' },
};

function RoleBadge({ role }) {
  const s = ROLE_STYLES[role] || ROLE_STYLES.student;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      background: s.bg, color: s.color,
      padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
      textTransform: 'capitalize',
    }}>
      {s.label}
    </span>
  );
}

function StatusBadge({ active }) {
  return (
    <span className={active ? 'badge badge-success' : 'badge badge-danger'}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const selectStyle = {
  padding: '10px 12px', background: 'var(--bg-tertiary)',
  border: '1px solid var(--border-primary)', borderRadius: 6,
  color: 'var(--text-primary)', fontSize: 14, minWidth: 150,
};

const iconBtnStyle = (color) => ({
  background: 'none',
  border: '1px solid var(--border-primary)',
  borderRadius: 6,
  color: color || 'var(--text-secondary)',
  cursor: 'pointer',
  padding: 6,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

function Users() {
  const { user } = useAuth();
  const currentUserId = user?._id || user?.id;
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [page, setPage] = useState(1);

  // Modal state
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);
  const [deactivateUser, setDeactivateUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);

  // Debounce the search input
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['users', search, roleFilter, activeFilter, page],
    queryFn: () => usersApi.list({
      search: search || undefined,
      role: roleFilter || undefined,
      isActive: activeFilter === '' ? undefined : activeFilter,
      page,
      limit: PAGE_SIZE,
    }),
    keepPreviousData: true,
  });

  const userList = data?.users || [];
  const pagination = data?.pagination || { page: 1, pages: 1, total: 0 };

  // Best-effort soft guard for the last active admin (backend is authoritative).
  const activeAdminCount = useMemo(
    () => userList.filter(u => u.role === 'admin' && u.isActive).length,
    [userList]
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  return (
    <div className="animate-fade-in" data-testid="users-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ marginBottom: 8 }}>User Management</h1>
          <p className="text-muted">
            Create and manage faculty and admin accounts. Students are listed read-only and
            managed via labs and sections.
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreate(true)}
          data-testid="add-user-button"
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <Plus size={18} /> Add User
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220, maxWidth: 340 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, email, or student ID..."
            data-testid="users-search"
            style={{ paddingLeft: 36, width: '100%' }}
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
          data-testid="users-role-filter"
          style={selectStyle}
        >
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="faculty">Faculty</option>
          <option value="student">Student</option>
        </select>
        <select
          value={activeFilter}
          onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }}
          data-testid="users-active-filter"
          style={selectStyle}
        >
          <option value="">All Statuses</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader size={32} className="spinner" />
        </div>
      ) : isError ? (
        <div role="alert" style={{
          padding: '12px 16px', borderRadius: 8,
          background: 'var(--btn-danger-bg)', border: '1px solid var(--accent-danger)',
          color: 'var(--accent-danger)', fontSize: 14,
        }}>
          {error?.response?.data?.message || 'Failed to load users'}
        </div>
      ) : userList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          <UserCog size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
          <p style={{ fontSize: 16, marginBottom: 8 }}>No users found</p>
          <p style={{ fontSize: 14 }}>Try adjusting your filters, or add a new faculty/admin account.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="table" data-testid="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Dept / Student ID</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Created</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {userList.map(u => {
                const isSelf = currentUserId && (u._id === currentUserId);
                const isStudent = u.role === 'student';
                const isLastActiveAdmin = u.role === 'admin' && u.isActive && activeAdminCount <= 1;

                // Guards (backend is authoritative; these mirror it for UX).
                const canDeactivate = !isStudent && !isSelf && !(u.isActive && isLastActiveAdmin);
                const canDelete = !isStudent && !isSelf && !isLastActiveAdmin;

                return (
                  <tr key={u._id} data-testid={`user-row-${u._id}`}>
                    <td style={{ fontWeight: 600 }}>
                      {u.name}
                      {isSelf && (
                        <span style={{
                          marginLeft: 8, fontSize: 11, fontWeight: 600,
                          color: 'var(--accent-primary)', background: 'var(--badge-info-bg)',
                          padding: '1px 6px', borderRadius: 4,
                        }}>
                          You
                        </span>
                      )}
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{u.email}</td>
                    <td><RoleBadge role={u.role} /></td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                      {isStudent
                        ? (u.studentId || '—')
                        : (u.department || '—')}
                    </td>
                    <td><StatusBadge active={u.isActive} /></td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{formatDate(u.lastLogin)}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{formatDate(u.createdAt)}</td>
                    <td>
                      {isStudent ? (
                        <span style={{ display: 'block', textAlign: 'right', color: 'var(--text-muted)', fontSize: 12, fontStyle: 'italic' }}>
                          Read-only
                        </span>
                      ) : (
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            title="Edit user"
                            aria-label={`Edit ${u.name}`}
                            data-testid={`user-edit-${u._id}`}
                            onClick={() => setEditUser(u)}
                            style={iconBtnStyle()}
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            title="Set or reset password"
                            aria-label={`Set password for ${u.name}`}
                            data-testid={`user-password-${u._id}`}
                            onClick={() => setPasswordUser(u)}
                            style={iconBtnStyle()}
                          >
                            <KeyRound size={15} />
                          </button>
                          {u.isActive ? (
                            <button
                              title={canDeactivate ? 'Deactivate user' : (isSelf ? 'You cannot deactivate yourself' : 'Cannot deactivate the last active admin')}
                              aria-label={`Deactivate ${u.name}`}
                              data-testid={`user-deactivate-${u._id}`}
                              onClick={() => setDeactivateUser(u)}
                              disabled={!canDeactivate}
                              style={{ ...iconBtnStyle('var(--accent-warning)'), opacity: canDeactivate ? 1 : 0.4, cursor: canDeactivate ? 'pointer' : 'not-allowed' }}
                            >
                              <UserX size={15} />
                            </button>
                          ) : (
                            <button
                              title="Reactivate user"
                              aria-label={`Reactivate ${u.name}`}
                              data-testid={`user-activate-${u._id}`}
                              onClick={() => setDeactivateUser(u)}
                              style={iconBtnStyle('var(--accent-success)')}
                            >
                              <UserCheck size={15} />
                            </button>
                          )}
                          <button
                            title={canDelete ? 'Delete user' : (isSelf ? 'You cannot delete yourself' : 'Cannot delete the last active admin')}
                            aria-label={`Delete ${u.name}`}
                            data-testid={`user-delete-${u._id}`}
                            onClick={() => setDeleteUser(u)}
                            disabled={!canDelete}
                            style={{ ...iconBtnStyle('var(--accent-danger)'), opacity: canDelete ? 1 : 0.4, cursor: canDelete ? 'pointer' : 'not-allowed' }}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && !isError && pagination.total > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <span className="text-muted" style={{ fontSize: 13 }} data-testid="users-pagination-info">
            {pagination.total} user{pagination.total === 1 ? '' : 's'} · Page {pagination.page} of {pagination.pages || 1}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-secondary"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              data-testid="users-prev-page"
              style={{ opacity: page <= 1 ? 0.5 : 1 }}
            >
              <ChevronLeft size={16} /> Prev
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= (pagination.pages || 1)}
              data-testid="users-next-page"
              style={{ opacity: page >= (pagination.pages || 1) ? 0.5 : 1 }}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); invalidate(); }}
        />
      )}
      {editUser && (
        <EditUserModal
          user={editUser}
          isSelf={currentUserId && editUser._id === currentUserId}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); invalidate(); }}
        />
      )}
      {passwordUser && (
        <SetPasswordModal
          user={passwordUser}
          onClose={() => setPasswordUser(null)}
          onSaved={() => { setPasswordUser(null); invalidate(); }}
        />
      )}
      {deactivateUser && (
        <DeactivateModal
          user={deactivateUser}
          onClose={() => setDeactivateUser(null)}
          onSaved={() => { setDeactivateUser(null); invalidate(); }}
        />
      )}
      {deleteUser && (
        <DeleteModal
          user={deleteUser}
          onClose={() => setDeleteUser(null)}
          onSaved={() => { setDeleteUser(null); invalidate(); }}
        />
      )}
    </div>
  );
}

/* ----------------------------- Create modal ----------------------------- */
function CreateUserModal({ onClose, onSaved }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('faculty');
  const [department, setDepartment] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (payload) => usersApi.create(payload),
    onSuccess: () => onSaved(),
    onError: (err) => setError(err.response?.data?.message || 'Failed to create user'),
  });

  const isPending = mutation.isPending;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Name is required'); return; }
    if (!email.trim()) { setError('Email is required'); return; }
    mutation.mutate({
      name: name.trim(),
      email: email.trim(),
      role,
      department: department.trim() || undefined,
      password: password ? password : undefined,
    });
  };

  return (
    <Dialog size="md" onClose={isPending ? undefined : onClose} labelledBy="create-user-title">
      <DialogHeader id="create-user-title" title="Add User" icon={<UserCog size={20} />} onClose={isPending ? undefined : onClose} />
      <form className="dlg-form" onSubmit={handleSubmit} aria-label="Create new user">
        <DialogBody>
          {error && <div role="alert" className="dlg-alert">{error}</div>}

          <Field label="Full Name" required>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              data-testid="user-name-input"
              required
            />
          </Field>

          <Field label="Email" required hint="Used for login and email OTP codes.">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane.doe@udst.edu.qa"
              data-testid="user-email-input"
              required
            />
          </Field>

          <div className="form-grid form-grid-2">
            <Field label="Role" required>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                data-testid="user-role-select"
                style={{ width: '100%' }}
              >
                <option value="faculty">Faculty</option>
                <option value="admin">Admin</option>
              </select>
            </Field>
            <Field label="Department">
              <input
                type="text"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="e.g., Computing"
                data-testid="user-department-input"
              />
            </Field>
          </div>

          <Field
            label="Password (optional)"
            hint="Leave blank for an OTP-only account — the user logs in with an email code. You can set a password later."
          >
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Leave blank for OTP-only login"
              data-testid="user-password-input"
              autoComplete="new-password"
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <button type="button" className="btn" onClick={onClose} disabled={isPending}>Cancel</button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isPending}
            data-testid="create-user-submit"
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {isPending ? <Loader size={16} className="spinner" /> : <Plus size={16} />}
            Create User
          </button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

/* ------------------------------ Edit modal ------------------------------ */
function EditUserModal({ user, isSelf, onClose, onSaved }) {
  const [name, setName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email || '');
  const [role, setRole] = useState(user.role || 'faculty');
  const [department, setDepartment] = useState(user.department || '');
  const [isActive, setIsActive] = useState(!!user.isActive);
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (payload) => usersApi.update(user._id, payload),
    onSuccess: () => onSaved(),
    onError: (err) => setError(err.response?.data?.message || 'Failed to update user'),
  });

  const isPending = mutation.isPending;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Name is required'); return; }
    if (!email.trim()) { setError('Email is required'); return; }
    const payload = {
      name: name.trim(),
      email: email.trim(),
      department: department.trim(),
    };
    // Self cannot change own role or active status (backend enforces this too).
    if (!isSelf) {
      payload.role = role;
      payload.isActive = isActive;
    }
    mutation.mutate(payload);
  };

  return (
    <Dialog size="md" onClose={isPending ? undefined : onClose} labelledBy="edit-user-title">
      <DialogHeader id="edit-user-title" title="Edit User" icon={<Pencil size={18} />} onClose={isPending ? undefined : onClose} />
      <form className="dlg-form" onSubmit={handleSubmit} aria-label="Edit user">
        <DialogBody>
          {error && <div role="alert" className="dlg-alert">{error}</div>}

          <Field label="Full Name" required>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-name-input" required />
          </Field>

          <Field label="Email" required>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="edit-email-input" required />
          </Field>

          <div className="form-grid form-grid-2">
            <Field label="Role" hint={isSelf ? 'You cannot change your own role.' : undefined}>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                disabled={isSelf}
                data-testid="edit-role-select"
                style={{ width: '100%', opacity: isSelf ? 0.6 : 1 }}
              >
                <option value="faculty">Faculty</option>
                <option value="admin">Admin</option>
              </select>
            </Field>
            <Field label="Department">
              <input type="text" value={department} onChange={(e) => setDepartment(e.target.value)} data-testid="edit-department-input" />
            </Field>
          </div>

          <SwitchRow
            id="edit-active"
            title="Active"
            description={isSelf ? 'You cannot change your own active status.' : 'Inactive users are blocked from logging in immediately.'}
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            disabled={isSelf}
          />
        </DialogBody>
        <DialogFooter>
          <button type="button" className="btn" onClick={onClose} disabled={isPending}>Cancel</button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isPending}
            data-testid="edit-user-submit"
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {isPending ? <Loader size={16} className="spinner" /> : <Pencil size={16} />}
            Save Changes
          </button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

/* --------------------------- Set password modal -------------------------- */
function SetPasswordModal({ user, onClose, onSaved }) {
  const [otpOnly, setOtpOnly] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (payload) => usersApi.setPassword(user._id, payload),
    onSuccess: () => onSaved(),
    onError: (err) => setError(err.response?.data?.message || 'Failed to update password'),
  });

  const isPending = mutation.isPending;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (otpOnly) {
      // Clear the password → revert to OTP-only login.
      mutation.mutate({ password: '' });
      return;
    }
    if (!password) { setError('Enter a password, or choose OTP-only login.'); return; }
    mutation.mutate({ password });
  };

  return (
    <Dialog size="sm" onClose={isPending ? undefined : onClose} labelledBy="set-password-title">
      <DialogHeader id="set-password-title" title="Set / Reset Password" icon={<KeyRound size={18} />} onClose={isPending ? undefined : onClose} />
      <form className="dlg-form" onSubmit={handleSubmit} aria-label="Set password">
        <DialogBody>
          {error && <div role="alert" className="dlg-alert">{error}</div>}

          <p className="text-muted" style={{ fontSize: 13, marginTop: 0 }}>
            Setting a password for <strong>{user.name}</strong> ({user.email}).
          </p>

          <SwitchRow
            id="set-password-otp-only"
            title="OTP-only login (clear password)"
            description="Remove any password so the user logs in with an email code only."
            checked={otpOnly}
            onChange={(e) => setOtpOnly(e.target.checked)}
          />

          {!otpOnly && (
            <>
              <Field label="New Password" required>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter a new password"
                  data-testid="set-password-input"
                  autoComplete="new-password"
                />
              </Field>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <button type="button" className="btn" onClick={onClose} disabled={isPending}>Cancel</button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isPending}
            data-testid="set-password-submit"
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {isPending ? <Loader size={16} className="spinner" /> : <KeyRound size={16} />}
            {otpOnly ? 'Clear Password' : 'Set Password'}
          </button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

/* --------------------------- Deactivate modal --------------------------- */
function DeactivateModal({ user, onClose, onSaved }) {
  const [error, setError] = useState('');
  const reactivating = !user.isActive;

  const mutation = useMutation({
    mutationFn: () => usersApi.update(user._id, { isActive: reactivating }),
    onSuccess: () => onSaved(),
    onError: (err) => setError(err.response?.data?.message || 'Action failed'),
  });

  const isPending = mutation.isPending;

  return (
    <Dialog size="sm" onClose={isPending ? undefined : onClose} labelledBy="deactivate-title" confirmOnClose={false}>
      <DialogHeader
        id="deactivate-title"
        title={reactivating ? 'Reactivate User' : 'Deactivate User'}
        icon={reactivating ? <UserCheck size={18} /> : <UserX size={18} />}
        onClose={isPending ? undefined : onClose}
      />
      <DialogBody>
        {error && <div role="alert" className="dlg-alert">{error}</div>}
        <p style={{ marginTop: 0 }}>
          {reactivating ? (
            <>Reactivate <strong>{user.name}</strong> ({user.email})? They will be able to log in again.</>
          ) : (
            <>Deactivate <strong>{user.name}</strong> ({user.email})? This immediately blocks login and is fully reversible.</>
          )}
        </p>
      </DialogBody>
      <DialogFooter>
        <button type="button" className="btn" onClick={onClose} disabled={isPending}>Cancel</button>
        <button
          type="button"
          className={reactivating ? 'btn btn-success' : 'btn btn-danger'}
          onClick={() => { setError(''); mutation.mutate(); }}
          disabled={isPending}
          data-testid="confirm-deactivate"
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          {isPending ? <Loader size={16} className="spinner" /> : (reactivating ? <UserCheck size={16} /> : <UserX size={16} />)}
          {reactivating ? 'Reactivate' : 'Deactivate'}
        </button>
      </DialogFooter>
    </Dialog>
  );
}

/* ----------------------------- Delete modal ----------------------------- */
function DeleteModal({ user, onClose, onSaved }) {
  const [error, setError] = useState('');
  const [ownedLabs, setOwnedLabs] = useState(null);

  const mutation = useMutation({
    mutationFn: () => usersApi.remove(user._id),
    onSuccess: () => onSaved(),
    onError: (err) => {
      const data = err.response?.data;
      setError(data?.message || 'Failed to delete user');
      // Surface the owns-labs 409 guard clearly.
      if (typeof data?.ownedLabs === 'number') setOwnedLabs(data.ownedLabs);
    },
  });

  const isPending = mutation.isPending;

  return (
    <Dialog size="sm" onClose={isPending ? undefined : onClose} labelledBy="delete-title" confirmOnClose={false}>
      <DialogHeader id="delete-title" title="Delete User" icon={<ShieldAlert size={18} />} onClose={isPending ? undefined : onClose} />
      <DialogBody>
        {error && (
          <div role="alert" className="dlg-alert">
            {error}
            {ownedLabs > 0 && (
              <div style={{ marginTop: 6, fontWeight: 400 }}>
                Reassign this user's {ownedLabs} lab{ownedLabs === 1 ? '' : 's'} to another owner,
                or deactivate the account instead.
              </div>
            )}
          </div>
        )}
        <p style={{ marginTop: 0 }}>
          Permanently delete <strong>{user.name}</strong> ({user.email})? This cannot be undone.
        </p>
        <p className="text-muted" style={{ fontSize: 13 }}>
          Prefer <strong>Deactivate</strong> — it is reversible and blocks login instantly. Delete is
          blocked if the user owns any labs.
        </p>
      </DialogBody>
      <DialogFooter>
        <button type="button" className="btn" onClick={onClose} disabled={isPending}>Cancel</button>
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => { setError(''); setOwnedLabs(null); mutation.mutate(); }}
          disabled={isPending}
          data-testid="confirm-delete"
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          {isPending ? <Loader size={16} className="spinner" /> : <Trash2 size={16} />}
          Delete Permanently
        </button>
      </DialogFooter>
    </Dialog>
  );
}

export default Users;
